<template>
  <div id="google-pay-button-container">
    <div ref="googlePay"/>
  </div>
</template>
<style>
  #google-pay-button-container {
    margin-bottom: 30px;
  }
</style>
<script>
export default {
  name: 'GooglePay',
  props: {
    merchant: {
      type: Object,
      require: true,
    },
    totalPrice: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    config: {
      /**
       * Define the version of the Google Pay API referenced when creating your
       * configuration
       *
       * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#PaymentDataRequest|apiVersion in PaymentDataRequest}
       */
      baseRequest: {
        apiVersion: 2,
        apiVersionMinor: 0,
      },
      /**
       * Identify your gateway and your site's gateway merchant identifier
       *
       * The Google Pay API response will return an encrypted payment method capable
       * of being charged by a supported gateway after payer authorization
       *
       * @todo check with your gateway on the parameters to pass
       * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#gateway|PaymentMethodTokenizationSpecification}
       */
      tokenizationSpecification: {
        type: 'PAYMENT_GATEWAY',
        parameters: {
          gateway: 'payarc',
          gatewayMerchantId: 'payarchostedcheckout',
        },
        // type: 'DIRECT', // "PAYMENT_GATEWAY",
        // parameters: {
        //   protocolVersion: 'ECv2',
        // },
      },
      /**
       * Card networks supported by your site and your gateway
       *
       * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#CardParameters|CardParameters}
       * @todo confirm card networks supported by your site and gateway
       */
      allowedCardNetworks: [
        'AMEX',
        'DISCOVER',
        // 'INTERAC',
        'JCB',
        'MASTERCARD',
        'VISA',
      ],
      /**
       * Card authentication methods supported by your site and your gateway
       *
       * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#CardParameters|CardParameters}
       * @todo confirm your processor supports Android device tokens for your
       * supported card networks
       */
      allowedCardAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
    },
  }),
  mounted() {
    this.config.tokenizationSpecification.parameters.publicKey = this.googleKey;
    setTimeout(() => this.loadGooglePay(), 500);
  },
  computed: {
    is_prod() {
      return this.$store.state.options.buildPurpose === 'release';
    },
  },
  methods: {
    loadGooglePay() {
      /**
       * Describe your site's support for the CARD payment method and its required
       * fields
       *
       * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#CardParameters|CardParameters}
       */
      const baseCardPaymentMethod = {
        type: 'CARD',
        parameters: {
          allowedAuthMethods: this.config.allowedCardAuthMethods,
          allowedCardNetworks: this.config.allowedCardNetworks,
          billingAddressRequired: true,
          billingAddressParameters: { format: 'FULL' },
        },
      };

      /**
       * Describe your site's support for the CARD payment method including optional
       * fields
       *
       * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#CardParameters|CardParameters}
       */
      const cardPaymentMethod = Object.assign({}, baseCardPaymentMethod, {
        tokenizationSpecification: this.config.tokenizationSpecification,
      });

      /**
       * An initialized google.payments.api.PaymentsClient object or null if not yet set
       *
       * @see {@link getGooglePaymentsClient}
       */
      let paymentsClient = null;

      let self = null;

      /**
       * Configure your site's support for payment methods supported by the Google Pay
       * API.
       *
       * Each member of allowedPaymentMethods should contain only the required fields,
       * allowing reuse of this base request when determining a viewer's ability
       * to pay and later requesting a supported payment method
       *
       * @returns {object} Google Pay API version, payment methods supported by the site
       */
      const getGoogleIsReadyToPayRequest = () => Object.assign({}, this.config.baseRequest, {
        allowedPaymentMethods: [baseCardPaymentMethod],
      });

      /**
       * Provide Google Pay API with a payment amount, currency, and amount status
       *
       * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#TransactionInfo|TransactionInfo}
       * @returns {object} transaction info, suitable for use as transactionInfo property of PaymentDataRequest
       */
      const getGoogleTransactionInfo = () => {
        const answer = {
          countryCode: 'US',
          currencyCode: 'USD',
          totalPriceStatus: 'FINAL',
          // set to cart total
          totalPrice: String(this.totalPrice),
        };

        if (this.merchant.surcharge) {
          const surcharge = this.merchant.surcharge / 100;

          answer.displayItems = [
            {
              label: 'Subtotal',
              type: 'SUBTOTAL',
              price: String(this.merchant.billing_amount / 100),
            },
            {
              label: 'Surcharge',
              type: 'TAX',
              price: String(surcharge),
            },
          ];

          answer.totalPriceLabel = 'Total';
        }

        return answer;
      };

      /**
       * Process payment data returned by the Google Pay API
       *
       * @param {object} paymentData response from Google Pay API after user approves payment
       * @see {@link https://developers.google.com/pay/api/web/reference/response-objects#PaymentData|PaymentData object reference}
       */
      function processPayment(paymentData) {
        return new Promise(((resolve, reject) => {
          // @todo pass payment token to your gateway to process payment
          // better solution - return data to parent component
          if (self) {
            self.$emit('google_payload', paymentData);
          }

          resolve({});
        }));
      }

      /**
       * Show Google Pay payment sheet when Google Pay payment button is clicked
       */
      function onGooglePaymentButtonClicked() {
        const paymentDataRequest = getGooglePaymentDataRequest();
        paymentDataRequest.transactionInfo = getGoogleTransactionInfo();

        const paymentsClient = getGooglePaymentsClient();
        paymentsClient
          .loadPaymentData(paymentDataRequest)
          .then((paymentData) => {
            // handle the response
          })
          .catch((err) => {
            // show error in developer console for debugging
            console.error(err);
          });
      }

      /**
       * Configure support for the Google Pay API
       *
       * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#PaymentDataRequest|PaymentDataRequest}
       * @returns {object} PaymentDataRequest fields
       */
      const getGooglePaymentDataRequest = () => {
        const paymentDataRequest = Object.assign({}, this.config.baseRequest);
        paymentDataRequest.allowedPaymentMethods = [cardPaymentMethod];
        paymentDataRequest.transactionInfo = getGoogleTransactionInfo();
        paymentDataRequest.callbackIntents = ['PAYMENT_AUTHORIZATION'];
        paymentDataRequest.merchantInfo = {
          merchantId: 'BCR2DN4T2SJ4JSKC',
          merchantName: this.merchant.name,
        };
        return paymentDataRequest;
      };

      function onPaymentAuthorized(paymentData) {
        return new Promise(((resolve, reject) => {
          processPayment(paymentData)
            .then(() => {
              resolve({ transactionState: 'SUCCESS' });
            })
            .catch(() => {
              resolve({
                transactionState: 'ERROR',
                error: {
                  intent: 'PAYMENT_AUTHORIZATION',
                  message: 'Insufficient funds',
                  reason: 'PAYMENT_DATA_INVALID',
                },
              });
            });
        }));
      }

      /**
       * Return an active PaymentsClient or initialize
       *
       * @see {@link https://developers.google.com/pay/api/web/reference/client#PaymentsClient|PaymentsClient constructor}
       * @returns {google.payments.api.PaymentsClient} Google Pay API client
       */
      const getGooglePaymentsClient = () => {
        if (paymentsClient === null) {
          paymentsClient = new google.payments.api.PaymentsClient({
            environment: this.is_prod ? 'PRODUCTION' : 'TEST',
            paymentDataCallbacks: {
              onPaymentAuthorized,
            },
          });
        }
        return paymentsClient;
      };

      /**
       * Add a Google Pay purchase button alongside an existing checkout button
       *
       * @see {@link https://developers.google.com/pay/api/web/reference/request-objects#ButtonOptions|Button options}
       * @see {@link https://developers.google.com/pay/api/web/guides/brand-guidelines|Google Pay brand guidelines}
       */
      const addGooglePayButton = () => {
        self = this;
        const paymentsClient = getGooglePaymentsClient();
        const button = paymentsClient.createButton({
          onClick: onGooglePaymentButtonClicked,
          buttonSizeMode: 'fill',
          buttonColor: 'black',
          buttonType: 'pay',
        });
        this.$refs.googlePay.appendChild(button);
      };

      /**
       * Initialize Google PaymentsClient after Google-hosted JavaScript has loaded
       *
       * Display a Google Pay payment button after confirmation of the viewer's
       * ability to pay.
       */
      function onGooglePayLoaded() {
        const paymentsClient = getGooglePaymentsClient();
        paymentsClient
          .isReadyToPay(getGoogleIsReadyToPayRequest())
          .then((response) => {
            if (response.result) {
              addGooglePayButton();
              // @todo prefetch payment data to improve performance after confirming site functionality
              // prefetchGooglePaymentData();
            }
          })
          .catch((err) => {
            // show error in developer console for debugging
            console.error(err);
          });
      }

      onGooglePayLoaded();
    },
  },
};
</script>
